import { getOffPlatformBookingPendingUrl } from "../offPlatformBooking";

import type { ScopedMutator } from "swr";

/**
 * Revalidates (refetches) every cached SWR key the predicate matches.
 *
 * Shared shape for the revalidation helpers below: non-string keys (SWR permits
 * array and object keys) can never match a URL, so they are rejected before the
 * predicate runs.
 *
 * Note: every caller matches by URL substring, so renaming a path segment on
 * the backend will silently change what gets invalidated.
 *
 * @param revalidateAll SWR's global mutate, used to match keys across the cache
 * @param matchesUrl receives each cached URL, returns whether to refetch it
 */
function revalidateMatchingUrlKeys(
  revalidateAll: ScopedMutator,
  matchesUrl: (url: string) => boolean,
): void {
  revalidateAll(
    (key) => (typeof key === "string" ? matchesUrl(key) : false),
    undefined,
    { revalidate: true },
  );
}

/**
 * Revalidates the cached detail and on-shipment quote data for a single
 * shipment, the one the user has open. Deliberately matches neither the
 * shipment list keys behind the quick shipment browser nor any other
 * shipment's detail key, so refreshing the open shipment leaves the list
 * beside it untouched.
 *
 * @param revalidateAll SWR's global mutate, used to match keys across the cache
 * @param shipmentId the open shipment; scopes invalidation to its keys only
 */
export function revalidateShipmentDetailCaches(
  revalidateAll: ScopedMutator,
  shipmentId: number,
): void {
  // Both role variants of each key end with these segments, so the shipper's
  // `/shippers/{roleId}` prefix needs no separate case.
  const detailUrl = `/shipments/${shipmentId}/condensed`;
  const quotesUrl = `/shipments/${shipmentId}/quotes`;
  // Backs the carrier's QuoteCountChip on the shipment overview. A separate
  // endpoint from the quotes list, so `quotesUrl` does not cover it.
  const quoteCountUrl = `/shipments/${shipmentId}/quote-count`;
  // The shipper's carrier logos. `quotesUrl` does not cover it either: the
  // segment is `quote-carrier-logos`, which contains no `quotes`. Without this
  // a quote arriving while the shipper watches refetches the quote rows but not
  // the logos, so the new row alone falls back to initials.
  const carrierLogosUrl = `/shipments/${shipmentId}/quote-carrier-logos`;
  // The shipper's pending off-platform booking alert. A report stops being
  // pending once the shipment is booked or cancelled, so it has to refetch
  // alongside the detail.
  const pendingOffPlatformBookingUrl =
    getOffPlatformBookingPendingUrl(shipmentId);

  revalidateMatchingUrlKeys(
    revalidateAll,
    (url) =>
      url.includes(detailUrl) ||
      url.includes(quotesUrl) ||
      url.includes(quoteCountUrl) ||
      url.includes(carrierLogosUrl) ||
      url.includes(pendingOffPlatformBookingUrl),
  );
}

/**
 * Revalidates cached shipment-detail and on-shipment quote data across *every*
 * shipment, including the shipment list keys that back the quick shipment
 * browser.
 *
 * Only for the user-initiated refresh affordances, where refreshing the whole
 * page (detail and list in one pass) is the intent. Anything reacting to a
 * background signal about one shipment wants
 * {@link revalidateShipmentDetailCaches} instead.
 *
 * @param revalidateAll SWR's global mutate, used to match keys across the cache
 */
export function revalidateShipmentDetailAndListCaches(
  revalidateAll: ScopedMutator,
): void {
  revalidateMatchingUrlKeys(
    revalidateAll,
    (url) =>
      url.includes("shipment") &&
      (url.includes("condensed") ||
        url.includes("quotes") ||
        url.includes("quote-count") ||
        // Not covered by "quotes" — see revalidateShipmentDetailCaches.
        url.includes("quote-carrier-logos") ||
        // See revalidateShipmentDetailCaches. Also refreshed after a cancel,
        // which runs onRefresh.
        url.includes("off-platform-bookings/pending") ||
        // The carrier partner list, which useShipmentsUrl builds without a
        // "condensed" segment. It is the default carrier view, so leaving it
        // out left most carriers' lists stale. The partner/public tab counts
        // are not covered here and do not need to be: they are fetched outside
        // SWR in ShipmentListContainer, which refreshes them itself.
        url.includes("shipments/partner")),
  );
}

/**
 * Revalidates the cached sent-follow-request data behind every add-partner
 * affordance, so a pending invite created in one place is reflected in all of
 * them without a remount.
 *
 * A carrier can have more than one quote on a shipment, meaning several quote
 * rows offer to add the same carrier. Without this, adding from one row leaves
 * the other rows offering the action again and a second click creates a
 * duplicate pending request: nothing rejects that server-side.
 *
 * Sweeps the cache rather than using the keyed mutate `useSentFollowRequests`
 * returns, because a row handed its list via the `sentRequests` prop has no key
 * of its own — the parent holds it, and only a cache-wide match reaches it.
 *
 * Matches the shipper's `sent` keys only: the carrier-side `received` list is a
 * different role's view and no shipper action invalidates it.
 *
 * @param revalidateAll SWR's global mutate, used to match keys across the cache
 */
export function revalidateSentFollowRequestCaches(
  revalidateAll: ScopedMutator,
): void {
  revalidateMatchingUrlKeys(revalidateAll, (url) =>
    url.includes("follow_requests/sent"),
  );
}

export function revalidateShipmentQaCaches(
  revalidateAll: ScopedMutator,
  shipmentId: number,
): void {
  const qaUrl = `/shipments/${shipmentId}/qa`;

  revalidateMatchingUrlKeys(revalidateAll, (url) => url.includes(qaUrl));
}

export function revalidateShipmentEtaCaches(
  revalidateAll: ScopedMutator,
  shipmentId: number,
): void {
  const etasUrl = `/shipments/${shipmentId}/list-etas`;

  revalidateMatchingUrlKeys(revalidateAll, (url) => url.includes(etasUrl));
}

/**
 * Revalidates every cached quote competitiveness score. The endpoint
 * (`/quotes/{quote_id}/competitiveness-score`) is keyed by quote id, not
 * shipment id, so — like {@link revalidateSentFollowRequestCaches} — this
 * matches a bare substring across the whole cache rather than scoping to one
 * shipment's quotes.
 *
 * @param revalidateAll SWR's global mutate, used to match keys across the cache
 */
export function revalidateQuoteCompetitivenessScoreCaches(
  revalidateAll: ScopedMutator,
): void {
  revalidateMatchingUrlKeys(revalidateAll, (url) =>
    url.includes("/competitiveness-score"),
  );
}
